import React from 'react';
import { motion } from 'framer-motion';
interface Point {
  x: number;
  y: number;
  label: string;
  color?: string;
}
interface CoordinateGridProps {
  width: number;
  height: number;
  points?: Point[];
  label?: string;
  showGrid?: boolean;
  mode?: 'stimulus' | 'support';
}
const POINT_COLORS = ['#6366f1', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#06b6d4' // cyan
];
export function CoordinateGrid({
  width,
  height,
  points = [],
  label,
  showGrid = true,
  mode = 'stimulus'
}: CoordinateGridProps) {
  const cellSize = 28;
  const padding = 50;
  const arrowSize = 8;
  const svgWidth = width * cellSize + padding * 2;
  const svgHeight = height * cellSize + padding * 2;
  // Origin position (bottom-left of grid area)
  const originX = padding;
  const originY = svgHeight - padding;
  // Convert grid coordinates to SVG coordinates
  const toSvgX = (x: number) => originX + x * cellSize;
  const toSvgY = (y: number) => originY - y * cellSize;
  return <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-7885">
      {label && <div className="text-sm font-medium text-slate-700 mb-3 text-center" data-id="element-7886">
          {label}
        </div>}
      <div className="flex justify-center" data-id="element-7887">
        <motion.svg width={svgWidth} height={svgHeight} role="img" aria-label={`Coördinatenstelsel ${width} bij ${height}${points.length > 0 ? ` met ${points.length} punt${points.length > 1 ? 'en' : ''}` : ''}`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3
      }} data-id="element-7888">
          {/* Grid lines */}
          {showGrid && <g className="grid-lines" data-id="element-7889">
              {/* Vertical grid lines */}
              {Array.from({
            length: width + 1
          }).map((_, i) => <line key={`v-${i}`} x1={toSvgX(i)} y1={toSvgY(0)} x2={toSvgX(i)} y2={toSvgY(height)} stroke="#e2e8f0" strokeWidth="1" data-id="element-7890" />)}
              {/* Horizontal grid lines */}
              {Array.from({
            length: height + 1
          }).map((_, i) => <line key={`h-${i}`} x1={toSvgX(0)} y1={toSvgY(i)} x2={toSvgX(width)} y2={toSvgY(i)} stroke="#e2e8f0" strokeWidth="1" data-id="element-7891" />)}
            </g>}

          {/* X-axis */}
          <g className="x-axis" data-id="element-7892">
            <line x1={originX - 10} y1={originY} x2={toSvgX(width) + 15} y2={originY} stroke="#475569" strokeWidth="2" data-id="element-7893" />
            {/* X-axis arrow */}
            <polygon points={`${toSvgX(width) + 15},${originY} ${toSvgX(width) + 15 - arrowSize},${originY - arrowSize / 2} ${toSvgX(width) + 15 - arrowSize},${originY + arrowSize / 2}`} fill="#475569" data-id="element-7894" />
            {/* X-axis label */}
            <text x={toSvgX(width) + 20} y={originY + 5} className="text-sm font-bold fill-slate-600" data-id="element-7895">
              x
            </text>
          </g>

          {/* Y-axis */}
          <g className="y-axis" data-id="element-7896">
            <line x1={originX} y1={originY + 10} x2={originX} y2={toSvgY(height) - 15} stroke="#475569" strokeWidth="2" data-id="element-7897" />
            {/* Y-axis arrow */}
            <polygon points={`${originX},${toSvgY(height) - 15} ${originX - arrowSize / 2},${toSvgY(height) - 15 + arrowSize} ${originX + arrowSize / 2},${toSvgY(height) - 15 + arrowSize}`} fill="#475569" data-id="element-7898" />
            {/* Y-axis label */}
            <text x={originX - 5} y={toSvgY(height) - 20} textAnchor="middle" className="text-sm font-bold fill-slate-600" data-id="element-7899">
              y
            </text>
          </g>

          {/* X-axis numbers */}
          {Array.from({
          length: width + 1
        }).map((_, i) => <text key={`x-num-${i}`} x={toSvgX(i)} y={originY + 20} textAnchor="middle" className="text-xs fill-slate-500" data-id="element-7900">
              {i}
            </text>)}

          {/* Y-axis numbers */}
          {Array.from({
          length: height + 1
        }).map((_, i) => <text key={`y-num-${i}`} x={originX - 15} y={toSvgY(i) + 4} textAnchor="middle" className="text-xs fill-slate-500" data-id="element-7901">
              {i}
            </text>)}

          {/* Points */}
          {points.map((point, index) => {
          const color = point.color || POINT_COLORS[index % POINT_COLORS.length];
          const px = toSvgX(point.x);
          const py = toSvgY(point.y);
          return <motion.g key={`point-${index}`} initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.2 + index * 0.1,
            duration: 0.3
          }} data-id="element-7902">
                {/* Point dot */}
                <circle cx={px} cy={py} r={8} fill={color} stroke="white" strokeWidth="2" data-id="element-7903" />
                {/* Point label */}
                <text x={px + 12} y={py - 8} className="text-sm font-bold" fill={color} data-id="element-7904">
                  {point.label}
                </text>
                {/* Coordinate display (in support mode) */}
                {mode === 'support' && <text x={px + 12} y={py + 6} className="text-xs fill-slate-500" data-id="element-7905">
                    ({point.x}, {point.y})
                  </text>}
              </motion.g>;
        })}
        </motion.svg>
      </div>
    </div>;
}